import { useCallback, useContext } from 'react';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import fiToDrillDowns from 'screens/DataManager/Metrics/MetricPopup/MetricForm/NormalMetricForm/fiToDrillDowns';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';

const transformFixedPlate = ({
  plate,
}: {
  plate: FixedFilterPlate;
}): FixedFilter[] => {
  return [plate.fixedValue];
};

const transformVariablePlate = ({
  plate,
  variableDrillDowns,
}: {
  plate: VariableFilterPlate;
  variableDrillDowns?: VariableDrillDownType[];
}): FixedFilter[] => {
  if (!variableDrillDowns) {
    return [];
  }

  const varId = plate.variableId;
  if (varId) {
    const variableDrill = variableDrillDowns.find((d) => d.id === varId);
    if (variableDrill) {
      return [variableDrill.value];
    } else {
      return [];
    }
  } else {
    return [];
  }
};

const transformDatasetPlate = ({
  plate,
  savedFilters,
  datasets,
}: {
  plate: SavedFilterPlate;
  savedFilters?: SavedFilter[];
  datasets?: FleetOps.DatasetDefinition[];
}): FixedFilter[] => {
  const savedFilter = savedFilters?.find(
    (df) => df.id === plate.datasetFilterId,
  );
  if (!savedFilter || !savedFilters || !datasets) {
    return [];
  }
  return fiToDrillDowns(savedFilter.filterInput, datasets);
};

const transformPlate = ({
  plate,
  variableDrillDowns,
  savedFilters,
  datasets,
}: {
  plate: FilterPlateType;
  variableDrillDowns?: VariableDrillDownType[];
  savedFilters?: SavedFilter[];
  datasets?: FleetOps.DatasetDefinition[];
}): FixedFilter[] => {
  if (filterPlateTypeCheckers.isExcludeTermedDrivers(plate)) {
    return [];
  } else if (filterPlateTypeCheckers.isSavedFilter(plate)) {
    return transformDatasetPlate({
      plate,
      savedFilters: savedFilters,
      datasets,
    });
  } else if (filterPlateTypeCheckers.isFixed(plate)) {
    return transformFixedPlate({ plate });
  } else if (filterPlateTypeCheckers.isVariable(plate)) {
    return transformVariablePlate({
      plate,
      variableDrillDowns,
    });
  } else {
    return [];
  }
};

const useToFixedFilters = () => {
  const { savedFilters } = useContext(SavedFiltersContext);
  const { datasets } = useContext(DatasetDefinitionsContext);

  const toFixedFilters = useCallback(
    ({
      plates,
      variableDrillDowns,
    }: {
      plates?: FilterPlateType[];
      variableDrillDowns?: VariableDrillDownType[];
    }): FixedFilter[] => {
      if (!plates) {
        return [];
      }

      return plates
        .map((plate) =>
          transformPlate({
            plate,
            variableDrillDowns,
            savedFilters: savedFilters,
            datasets,
          }),
        )
        .reduce((a, b) => [...a, ...b], []);
    },
    [savedFilters, datasets],
  );

  return toFixedFilters;
};

export const toFixedPlate = ({
  plate,
  variableDrillDowns,
}: {
  plate: VariableFilterPlate;
  variableDrillDowns: VariableDrillDownType[];
}): FilterPlateType => {
  const v = variableDrillDowns.find((d) => d.id === plate.variableId);
  if (v) {
    return {
      id: plate.id,
      type: 'Fixed',
      fixedValue: v.value,
    };
  } else {
    return plate;
  }
};

export default useToFixedFilters;
